import type { z } from 'zod';

import { normalizeDocument } from './document';
import {
  addressSchema,
  bankAccountSchema,
  documentCaptureSchema,
  documentSchema,
  faceDetectionSchema,
  incomeSchema,
  legalEntityIncomeSchema,
  legalEntityRelationSchema,
  occupationSchema,
  pepSchema,
  phoneSchema,
  representativeDocumentFileSchema,
  socialContractSchema,
  termsSchema
} from './registry';

// The wizard step identifiers shared between frontend (UI) and backend
// (validation). Keep this list in sync with `apps/front/src/components/Step/registry.tsx`.
export const STEP_NAMES = [
  'legal-entity-relation',
  'occupation-natural',
  'occupation-partner',
  'occupation-representative',
  'income-natural',
  'income-partner',
  'income-representative',
  'legal-entity-income',
  'pep-natural',
  'pep-partner',
  'pep-representative',
  'face-detection',
  'representative-document-file',
  'address',
  'bank-account',
  'document-not-found-capture',
  'terms',
  'document-natural',
  'document-partner',
  'document-representative',
  'phone-natural',
  'phone-representative',
  'phone-partner',
  'social-contract-partner',
  'social-contract-representative'
] as const;

export type StepName = (typeof STEP_NAMES)[number];

export const STEP_VALIDATORS: Record<StepName, z.ZodType> = {
  'legal-entity-relation': legalEntityRelationSchema,
  'occupation-natural': occupationSchema,
  'occupation-partner': occupationSchema,
  'occupation-representative': occupationSchema,
  'income-natural': incomeSchema,
  'income-partner': incomeSchema,
  'income-representative': incomeSchema,
  'legal-entity-income': legalEntityIncomeSchema,
  'pep-natural': pepSchema,
  'pep-partner': pepSchema,
  'pep-representative': pepSchema,
  'face-detection': faceDetectionSchema,
  'representative-document-file': representativeDocumentFileSchema,
  'address': addressSchema,
  'bank-account': bankAccountSchema,
  'terms': termsSchema,
  'document-natural': documentSchema,
  'document-partner': documentSchema,
  'document-representative': documentSchema,
  'phone-natural': phoneSchema,
  'phone-representative': phoneSchema,
  'phone-partner': phoneSchema,
  'social-contract-partner': socialContractSchema,
  'social-contract-representative': socialContractSchema,
  'document-not-found-capture': documentCaptureSchema
};

export const FLOW_NAMES = ['natural-person', 'legal-entity', 'document-not-found'] as const;

export type FlowName = (typeof FLOW_NAMES)[number];

const CPF_LENGTH = 11;
const CNPJ_LENGTH = 14;

export const pickFlow = (document?: string | null): FlowName => {
  const normalized = normalizeDocument(document ?? '');

  if (normalized.length === CNPJ_LENGTH) {
    return 'legal-entity';
  }

  if (normalized.length === CPF_LENGTH) {
    return 'natural-person';
  }

  return 'document-not-found';
};
